import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';

// ============ TYPES ============

interface CraftingIngredient {
  itemId: string;
  quantity: number;
  itemName?: string;
  playerHas?: number;
}

interface CraftingRecipe {
  id: string;
  name: string;
  description: string;
  resultItemId: string;
  resultQuantity: number;
  resultType: 'module' | 'item' | 'consumable';
  ingredients: CraftingIngredient[];
  craftingTime: number;
  levelRequired: number;
  pieceCost: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  category: 'armament' | 'hull' | 'rigging' | 'consumable' | 'ammunition' | 'construction';
}

interface CraftingStatus {
  isCrafting: boolean;
  recipeId?: string;
  recipeName?: string;
  startTime?: number;
  endTime?: number;
  timeRemaining?: number;
  quantity?: number;
  currentBatch?: number;
  resultPending?: boolean;
  resultItemId?: string;
  resultQuantity?: number;
  resultType?: 'module' | 'item' | 'consumable';
  rarity?: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  activeCrafts?: Array<{
    recipeId: string;
    recipeName: string;
    startTime: number;
    endTime: number;
    quantity?: number;
    resultItemId?: string;
    resultQuantity?: number;
    resultType?: 'module' | 'item' | 'consumable';
    rarity?: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  }>;
}

interface CraftingPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  playerLevel: number;
  pieces: number;
  unlockedBlueprints?: Set<string>;
}

// ============ CONSTANTS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const CATEGORY_LABELS: Record<string, string> = {
  armament: 'Armament Modules',
  hull: 'Hull Modules',
  rigging: 'Rigging Modules',
  consumable: 'Consumables',
  ammunition: 'Ammunition',
  construction: 'Construction Kits',
};

const CATEGORY_COLORS: Record<string, string> = {
  armament: '#e74c3c',
  hull: '#3498db',
  rigging: '#2ecc71',
  consumable: '#f39c12',
  ammunition: '#95a5a6',
  construction: '#8b4513',
};

// ============ STYLES ============

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    fontFamily: 'var(--font-mono), monospace',
  },
  panel: {
    backgroundColor: '#0d1117',
    border: '2px solid #30363d',
    width: '800px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#e6edf3',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22',
    cursor: 'grab',
    userSelect: 'none',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#58a6ff',
    pointerEvents: 'none',
  },
  closeBtn: {
    background: 'none',
    border: '1px solid #f85149',
    color: '#f85149',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px 8px',
    fontFamily: 'var(--font-mono), monospace',
    fontWeight: 'bold',
  },
  content: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  leftPanel: {
    flex: 1,
    overflowY: 'auto',
    borderRight: '1px solid #30363d',
  },
  rightPanel: {
    width: '300px',
    padding: '16px',
    overflowY: 'auto',
    backgroundColor: '#161b22',
  },
  categorySection: {
    borderBottom: '1px solid #21262d',
  },
  categoryHeader: {
    padding: '10px 16px',
    backgroundColor: '#21262d',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  recipeList: {
    padding: '8px',
  },
  recipeItem: {
    padding: '10px 12px',
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    marginBottom: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'border-color 0.2s',
  },
  recipeInfo: {
    flex: 1,
  },
  recipeName: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '2px',
  },
  recipeMeta: {
    fontSize: '11px',
    color: '#8b949e',
  },
  recipeCost: {
    textAlign: 'right',
    fontSize: '12px',
  },
  selectedRecipe: {
    padding: '16px',
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    marginBottom: '16px',
  },
  ingredientList: {
    marginTop: '12px',
  },
  ingredientItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: '1px solid #30363d',
    fontSize: '12px',
  },
  ingredientName: {
    color: '#e6edf3',
  },
  ingredientQty: {
    fontWeight: 'bold',
  },
  craftBtn: {
    width: '100%',
    padding: '12px',
    border: 'none',
    backgroundColor: '#238636',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: '12px',
  },
  craftBtnDisabled: {
    backgroundColor: '#21262d',
    color: '#8b949e',
    cursor: 'not-allowed',
  },
  progressSection: {
    padding: '16px',
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    marginBottom: '16px',
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#30363d',
    marginTop: '8px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#238636',
    transition: 'width 0.5s',
  },
  message: {
    padding: '8px 12px',
    backgroundColor: '#21262d',
    borderLeft: '3px solid #58a6ff',
    marginBottom: '12px',
    fontSize: '12px',
  },
  filterBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: '1px solid #30363d',
    flexWrap: 'wrap',
  },
  filterBtn: {
    padding: '6px 12px',
    border: '1px solid #30363d',
    backgroundColor: 'transparent',
    color: '#8b949e',
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  filterBtnActive: {
    borderColor: '#58a6ff',
    color: '#58a6ff',
    backgroundColor: 'rgba(88, 166, 255, 0.1)',
  },
  pendingSection: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#1a1f26',
    border: '1px solid #238636',
    borderRadius: 0,
  },
  pendingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#238636',
  },
  pendingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
  },
  pendingItem: {
    width: '48px',
    height: '48px',
    backgroundColor: '#21262d',
    border: '2px solid #30363d',
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
    overflow: 'hidden',
  },
  pendingItemIcon: {
    width: '36px',
    height: '36px',
    objectFit: 'contain' as const,
  },
  pendingItemFallback: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#e6edf3',
    border: '1px solid #30363d',
    backgroundColor: '#0d1117',
    borderRadius: 0,
    userSelect: 'none' as const,
  },
  pendingItemQuantity: {
    position: 'absolute' as const,
    bottom: '2px',
    right: '2px',
    fontSize: '10px',
    fontWeight: 'bold',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: '1px 4px',
    borderRadius: 0,
  },
  craftingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  craftingIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: '#0d1117',
    border: '2px solid #30363d',
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  craftingInfo: {
    flex: 1,
  },
  collectBtn: {
    width: '100%',
    padding: '12px',
    border: 'none',
    backgroundColor: '#238636',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: '12px',
    animation: 'pulse 2s infinite',
  },
  activeList: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    maxHeight: '220px',
    overflowY: 'auto' as const,
  },
  activeListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    fontSize: '12px',
    color: '#8b949e',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
  },
  activeItem: {
    border: '1px solid #30363d',
    padding: '8px',
    marginBottom: '8px',
    backgroundColor: '#0d1117',
  },
  activeItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  activeItemTitle: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#e6edf3',
  },
  activeItemTime: {
    fontSize: '11px',
    color: '#8b949e',
  },
};

// ============ COMPONENT ============

export const CraftingPanel: React.FC<CraftingPanelProps> = ({
  socket,
  isOpen,
  onClose,
  playerLevel,
  pieces,
  unlockedBlueprints = new Set(),
}) => {
  const [recipes, setRecipes] = useState<CraftingRecipe[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<CraftingRecipe | null>(null);
  const [craftingStatus, setCraftingStatus] = useState<CraftingStatus>({ isCrafting: false });
  const [message, setMessage] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['armament', 'hull', 'rigging']));
  const [craftQuantity, setCraftQuantity] = useState<number>(1);
  const [missingIconKeys, setMissingIconKeys] = useState<Set<string>>(new Set());
  const [clock, setClock] = useState<number>(Date.now());
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(150, 150, {
    storageKey: 'panel:crafting',
    clampToViewport: true,
  });

  const getResultIconKey = useCallback((resultType: string | undefined, resultItemId: string | undefined): string => {
    return `${resultType || 'unknown'}:${resultItemId || 'unknown'}`;
  }, []);

  const getResultIconPath = useCallback((resultType: string | undefined, resultItemId: string | undefined): string => {
    const safeResultType: string = typeof resultType === 'string' ? resultType : 'item';
    const safeItemId: string = typeof resultItemId === 'string' ? resultItemId : 'unknown';
    const folder: string = safeResultType === 'module'
      ? 'modules'
      : safeResultType === 'consumable'
        ? 'consumables'
        : 'materials';
    return `/assets/items/${folder}/${safeItemId}.png`;
  }, []);

  const markIconMissing = useCallback((key: string): void => {
    setMissingIconKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleRecipes = (data: { recipes: CraftingRecipe[] }): void => {
      setRecipes(data.recipes);
    };

    const handleCraftingStatus = (data: CraftingStatus): void => {
      setCraftingStatus(data);
    };

    const handleCraftingStarted = (data: { recipeName: string; endTime: number }): void => {
      setMessage(`Started crafting ${data.recipeName}`);
      setTimeout(() => setMessage(''), 3000);

      const startedAt: number = Date.now();
      const matchingRecipe = selectedRecipe || recipes.find(r => r.name === data.recipeName);
      const newActive = {
        recipeId: matchingRecipe?.id || data.recipeName,
        recipeName: data.recipeName,
        startTime: startedAt,
        endTime: data.endTime,
        quantity: matchingRecipe ? craftQuantity : 1,
        resultItemId: matchingRecipe?.resultItemId,
        resultQuantity: matchingRecipe?.resultQuantity,
        resultType: matchingRecipe?.resultType,
        rarity: matchingRecipe?.rarity,
      };

      setCraftingStatus(prev => ({
        ...prev,
        isCrafting: true,
        recipeId: matchingRecipe?.id || prev.recipeId,
        recipeName: data.recipeName,
        startTime: startedAt,
        endTime: data.endTime,
        timeRemaining: Math.max(0, Math.ceil((data.endTime - Date.now()) / 1000)),
        quantity: matchingRecipe ? craftQuantity : prev.quantity,
        resultItemId: matchingRecipe?.resultItemId || prev.resultItemId,
        resultQuantity: matchingRecipe?.resultQuantity || prev.resultQuantity,
        resultType: matchingRecipe?.resultType || prev.resultType,
        rarity: matchingRecipe?.rarity || prev.rarity,
        activeCrafts: [...(prev.activeCrafts || []), newActive],
      }));
    };

    const handleCraftingCompleted = (data: { recipeName: string }): void => {
      setMessage(`Completed: ${data.recipeName}`);
      setCraftingStatus({ isCrafting: false });
      setTimeout(() => setMessage(''), 3000);
    };

    const handleCraftingReady = (data: { 
      recipeId: string;
      recipeName: string; 
      resultItemId: string; 
      resultQuantity: number;
      resultType?: 'module' | 'item' | 'consumable';
      rarity?: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
      quantity?: number;
    }): void => {
      // Craft completed while offline - show notification and update status
      setMessage(`Crafting complete: ${data.recipeName} is ready to collect!`);
      setCraftingStatus(prev => ({ 
        ...prev, 
        isCrafting: true, 
        resultPending: true, 
        recipeId: data.recipeId,
        recipeName: data.recipeName, 
        timeRemaining: 0,
        resultItemId: data.resultItemId,
        resultQuantity: data.resultQuantity,
        resultType: data.resultType,
        rarity: data.rarity,
        quantity: data.quantity || 1,
      }));
    };

    socket.on('crafting:recipes', handleRecipes);
    socket.on('crafting:status', handleCraftingStatus);
    socket.on('crafting:started', handleCraftingStarted);
    socket.on('crafting:completed', handleCraftingCompleted);
    socket.on('crafting:ready', handleCraftingReady);

    return () => {
      socket.off('crafting:recipes', handleRecipes);
      socket.off('crafting:status', handleCraftingStatus);
      socket.off('crafting:started', handleCraftingStarted);
      socket.off('crafting:completed', handleCraftingCompleted);
      socket.off('crafting:ready', handleCraftingReady);
    };
  }, [socket, selectedRecipe, craftQuantity, recipes]);

  // Request data when opened
  useEffect(() => {
    if (isOpen && socket) {
      socket.emit('crafting:get_recipes');
      socket.emit('crafting:get_status');
    }
  }, [isOpen, socket]);

  // Update crafting timer
  useEffect(() => {
    if (!craftingStatus.isCrafting || !craftingStatus.endTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((craftingStatus.endTime! - now) / 1000));
      setCraftingStatus(prev => ({ ...prev, timeRemaining: remaining }));

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [craftingStatus.isCrafting, craftingStatus.endTime]);

  // Global tick for active crafts render (per-second)
  useEffect(() => {
    const interval = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const showMessage = useCallback((msg: string): void => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  const handleStartCraft = (): void => {
    if (!socket || !selectedRecipe) return;
    socket.emit('crafting:start', { recipeId: selectedRecipe.id, quantity: craftQuantity }, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
      if (response.success) {
        setCraftQuantity(1); // Reset quantity after starting
      }
    });
  };

  // Calculate max craftable quantity based on materials and pieces
  const getMaxCraftable = (recipe: CraftingRecipe): number => {
    if (playerLevel < recipe.levelRequired) return 0;
    
    // Max based on pieces
    const maxByPieces = Math.floor(pieces / recipe.pieceCost);
    
    // Max based on ingredients
    let maxByIngredients = Infinity;
    for (const ing of recipe.ingredients) {
      const playerHas = ing.playerHas ?? 0;
      const maxForThis = Math.floor(playerHas / ing.quantity);
      maxByIngredients = Math.min(maxByIngredients, maxForThis);
    }
    
    return Math.min(maxByPieces, maxByIngredients === Infinity ? 0 : maxByIngredients);
  };

  const handleCompleteCraft = (): void => {
    if (!socket) return;
    socket.emit('crafting:complete', {}, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
    });
  };

  const handleCancelCraft = (): void => {
    if (!socket) return;
    socket.emit('crafting:cancel', {}, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
    });
  };

  const toggleCategory = (category: string): void => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const hasRequiredMaterials = (recipe: CraftingRecipe, quantity: number): boolean => {
    return recipe.ingredients.every((ing) => {
      const have: number = ing.playerHas ?? 0;
      return have >= ing.quantity * quantity;
    });
  };

  const canCraft = (recipe: CraftingRecipe): boolean => {
    if (playerLevel < recipe.levelRequired) return false;
    if (pieces < recipe.pieceCost * craftQuantity) return false;
    if (!hasRequiredMaterials(recipe, craftQuantity)) return false;
    return true;
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getMissingMaterials = (recipe: CraftingRecipe, quantity: number): Array<{ name: string; needed: number; have: number }> => {
    return recipe.ingredients
      .map((ing) => {
        const have: number = ing.playerHas ?? 0;
        const needed: number = ing.quantity * quantity;
        return { name: ing.itemName || ing.itemId, needed, have };
      })
      .filter((mat) => mat.have < mat.needed);
  };

  const getActiveCraftsList = (): CraftingStatus['activeCrafts'] => {
    if (craftingStatus.activeCrafts && craftingStatus.activeCrafts.length > 0) {
      return craftingStatus.activeCrafts;
    }
    if (craftingStatus.isCrafting && craftingStatus.recipeId && craftingStatus.recipeName && craftingStatus.startTime && craftingStatus.endTime) {
      return [
        {
          recipeId: craftingStatus.recipeId,
          recipeName: craftingStatus.recipeName,
          startTime: craftingStatus.startTime,
          endTime: craftingStatus.endTime,
          quantity: craftingStatus.quantity,
          resultItemId: craftingStatus.resultItemId,
          resultQuantity: craftingStatus.resultQuantity,
          resultType: craftingStatus.resultType,
          rarity: craftingStatus.rarity,
        },
      ];
    }
    return [];
  };

  const groupedRecipes = recipes.reduce((acc, recipe) => {
    if (!acc[recipe.category]) acc[recipe.category] = [];
    acc[recipe.category].push(recipe);
    return acc;
  }, {} as Record<string, CraftingRecipe[]>);

  // Sort recipes within each category: blueprint-unlocked on top, then by level
  const sortedGroupedRecipes = Object.entries(groupedRecipes).reduce((acc, [category, categoryRecipes]) => {
    const sorted = [...categoryRecipes].sort((a, b) => {
      const aUnlocked = unlockedBlueprints.has(a.id);
      const bUnlocked = unlockedBlueprints.has(b.id);
      
      // Blueprint-unlocked recipes on top
      if (aUnlocked && !bUnlocked) return -1;
      if (!aUnlocked && bUnlocked) return 1;
      
      // Then sort by level requirement
      return a.levelRequired - b.levelRequired;
    });
    acc[category] = sorted;
    return acc;
  }, {} as Record<string, CraftingRecipe[]>);

  const filteredCategories = categoryFilter
    ? { [categoryFilter]: sortedGroupedRecipes[categoryFilter] || [] }
    : sortedGroupedRecipes;

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        ...dragStyle,
        zIndex: 1000,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header} onMouseDown={handleHeaderMouseDown}>
          <h2 style={{...styles.title}} className="font-lookout-24">Crafting Workshop</h2>
          <span style={{ color: '#f0b429', marginRight: 'auto', marginLeft: '16px' }}>{pieces.toLocaleString()}₱</span>
          <span style={{ color: '#8b949e', marginRight: '16px', fontSize: '12px' }}>
            Level {playerLevel}
          </span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Filter Bar */}
        <div style={styles.filterBar}>
          <button
            style={{ ...styles.filterBtn, ...(categoryFilter === null ? styles.filterBtnActive : {}) }}
            onClick={() => setCategoryFilter(null)}
          >
            All
          </button>
          {Object.keys(CATEGORY_LABELS).map((cat) => (
            <button
              key={cat}
              style={{
                ...styles.filterBtn,
                ...(categoryFilter === cat ? styles.filterBtnActive : {}),
                borderColor: categoryFilter === cat ? CATEGORY_COLORS[cat] : undefined,
                color: categoryFilter === cat ? CATEGORY_COLORS[cat] : undefined,
              }}
              onClick={() => setCategoryFilter(cat)}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {/* Message */}
        {message && <div style={styles.message}>{message}</div>}

        {/* Content */}
        <div style={styles.content}>
          {/* Left Panel - Recipe List */}
          <div style={styles.leftPanel}>
            {Object.entries(filteredCategories).map(([category, categoryRecipes]) => (
              <div key={category} style={styles.categorySection}>
                <div
                  style={styles.categoryHeader}
                  onClick={() => toggleCategory(category)}
                >
                  <span style={{ color: CATEGORY_COLORS[category] }}>■</span>
                  <span>{CATEGORY_LABELS[category]}</span>
                  <span style={{ color: '#8b949e', fontSize: '11px' }}>
                    ({categoryRecipes?.length || 0})
                  </span>
                  <span style={{ marginLeft: 'auto', color: '#8b949e' }}>
                    {expandedCategories.has(category) ? 'v' : '>'}
                  </span>
                </div>
                {expandedCategories.has(category) && (
                  <div style={styles.recipeList}>
                    {categoryRecipes?.map(recipe => {
                      const isLocked = playerLevel < recipe.levelRequired;
                      const isSelected = selectedRecipe?.id === recipe.id;
                      return (
                        <div
                          key={recipe.id}
                          style={{
                            ...styles.recipeItem,
                            borderColor: isSelected ? '#58a6ff' : '#30363d',
                            opacity: isLocked ? 0.5 : 1,
                          }}
                          onClick={() => {
                            if (!isLocked) {
                              setSelectedRecipe(recipe);
                              setCraftQuantity(1); // Reset quantity when selecting new recipe
                            }
                          }}
                        >
                          <div style={styles.recipeInfo}>
                            <div style={{ ...styles.recipeName, color: RARITY_COLORS[recipe.rarity] }}>
                              {recipe.name}
                              {recipe.resultQuantity > 1 && ` x${recipe.resultQuantity}`}
                            </div>
                            <div style={styles.recipeMeta}>
                              {isLocked ? `Requires Level ${recipe.levelRequired}` : `Time: ${formatTime(recipe.craftingTime)}`}
                            </div>
                          </div>
                          <div style={styles.recipeCost}>
                            <div style={{ color: '#f0b429' }}>₱{recipe.pieceCost}</div>
                            <div style={{ color: '#8b949e', fontSize: '10px' }}>
                              {recipe.ingredients.length} materials
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Panel - Details */}
          <div style={styles.rightPanel}>
            {/* Crafting Progress - Enhanced with icon and better layout */}
            {craftingStatus.isCrafting && (() => {
              const craftingRecipe = recipes.find(r => r.id === craftingStatus.recipeId);
              const isReady = craftingStatus.timeRemaining === 0 || craftingStatus.resultPending;
              const progressPercent = craftingStatus.endTime && craftingStatus.startTime
                ? Math.min(100, ((Date.now() - craftingStatus.startTime) / (craftingStatus.endTime - craftingStatus.startTime)) * 100)
                : 0;
              const totalResultQty = (craftingRecipe?.resultQuantity || 1) * (craftingStatus.quantity || 1);
              
              return (
                <div style={{
                  ...styles.progressSection,
                  borderColor: isReady ? '#238636' : '#30363d',
                  boxShadow: isReady ? '0 0 10px rgba(35, 134, 54, 0.3)' : 'none',
                }}>
                  {/* Header with icon */}
                  <div style={styles.craftingHeader}>
                    <div style={{
                      ...styles.craftingIcon,
                      borderColor: craftingRecipe ? RARITY_COLORS[craftingRecipe.rarity] : '#30363d',
                    }}>
                      {craftingRecipe && (() => {
                        const iconKey: string = getResultIconKey(craftingRecipe.resultType, craftingRecipe.resultItemId);
                        const iconPath: string = getResultIconPath(craftingRecipe.resultType, craftingRecipe.resultItemId);
                        const isMissing: boolean = missingIconKeys.has(iconKey);
                        const renderItemIcon = (icon: string | undefined, name: string): JSX.Element => {
                          const publicUrl: string = process.env.PUBLIC_URL || '';
                          const resolvedSrc = icon && icon.startsWith('/assets')
                            ? `${publicUrl}${icon}`
                            : icon && (icon.startsWith('http://') || icon.startsWith('https://'))
                            ? icon
                            : `${publicUrl}/assets/unknown.png`;
                          return (
                            <img
                              src={resolvedSrc}
                              alt={name}
                              style={{ width: 32, height: 32, objectFit: 'contain' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target.src !== `${publicUrl}/assets/unknown.png`) {
                                  target.onerror = null;
                                  target.src = `${publicUrl}/assets/unknown.png`;
                                }
                              }}
                            />
                          );
                        };
                        if (isMissing) {
                          return <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#e6edf3' }}>[C]</span>;
                        }
                        return renderItemIcon(iconPath, craftingRecipe.name);
                      })()}
                    </div>
                    <div style={styles.craftingInfo}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold', 
                        color: craftingRecipe ? RARITY_COLORS[craftingRecipe.rarity] : '#e6edf3',
                        marginBottom: '2px',
                      }}>
                        {isReady ? '[READY] ' : '[CRAFT] '}{craftingStatus.recipeName}
                        {(craftingStatus.quantity || 1) > 1 && ` x${craftingStatus.quantity}`}
                      </div>
                      <div style={{ fontSize: '12px', color: isReady ? '#238636' : '#8b949e' }}>
                        {isReady
                          ? `Ready to collect! (${totalResultQty}x)`
                          : `${formatTime(craftingStatus.timeRemaining || 0)} remaining`}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    ...styles.progressBar,
                    height: '10px',
                    borderRadius: 0,
                  }}>
                    <div
                      style={{
                        ...styles.progressFill,
                        width: isReady ? '100%' : `${progressPercent}%`,
                        backgroundColor: isReady ? '#238636' : '#58a6ff',
                        borderRadius: 0,
                      }}
                    />
                  </div>

                  {/* Pending Collection Grid - shows what will be collected */}
                  {isReady && craftingRecipe && (
                    <div style={styles.pendingSection}>
                      <div style={styles.pendingHeader}>
                        <span>ITEMS</span>
                        <span>Ready to Collect</span>
                      </div>
                      <div style={styles.pendingGrid}>
                        {Array.from({ length: Math.min(totalResultQty, 8) }).map((_, idx) => (
                          <div 
                            key={idx} 
                            style={{
                              ...styles.pendingItem,
                              borderColor: RARITY_COLORS[craftingRecipe.rarity],
                            }}
                          >
                            {(() => {
                              const iconKey: string = getResultIconKey(craftingRecipe.resultType, craftingRecipe.resultItemId);
                              const iconPath: string = getResultIconPath(craftingRecipe.resultType, craftingRecipe.resultItemId);
                              const isMissing: boolean = missingIconKeys.has(iconKey);
                              if (isMissing) {
                                const fallbackLabel: string = (craftingRecipe.name || 'Item').charAt(0).toUpperCase();
                                return <div style={styles.pendingItemFallback}>{fallbackLabel}</div>;
                              }
                              return (
                                <img
                                  src={iconPath}
                                  alt={craftingRecipe.name}
                                  style={styles.pendingItemIcon}
                                  onError={() => {
                                    markIconMissing(iconKey);
                                  }}
                                />
                              );
                            })()}
                            {idx === 0 && totalResultQty > 1 && (
                              <span style={styles.pendingItemQuantity}>
                                {totalResultQty > 8 ? `${totalResultQty}` : ''}
                              </span>
                            )}
                          </div>
                        ))}
                        {totalResultQty > 8 && (
                          <div style={{
                            ...styles.pendingItem,
                            backgroundColor: '#161b22',
                            fontSize: '11px',
                            color: '#8b949e',
                          }}>
                            +{totalResultQty - 8}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  {isReady ? (
                    <button 
                      style={{
                        ...styles.collectBtn,
                        background: 'linear-gradient(135deg, #238636 0%, #2ea043 100%)',
                      }} 
                      onClick={handleCompleteCraft}
                    >
                      COLLECT {totalResultQty > 1 ? `(${totalResultQty}x)` : ''}
                    </button>
                  ) : (
                    <button
                      style={{ ...styles.craftBtn, backgroundColor: '#da3633' }}
                      onClick={handleCancelCraft}
                    >
                      CANCEL (Materials Lost)
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Selected Recipe */}
            {selectedRecipe && !craftingStatus.isCrafting && (
              <div style={styles.selectedRecipe}>
                <div style={{ fontSize: '15px', fontWeight: 'bold', color: RARITY_COLORS[selectedRecipe.rarity], marginBottom: '4px' }}>
                  {selectedRecipe.name}
                </div>
                <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px' }}>
                  {selectedRecipe.description}
                </div>
                <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#8b949e' }}>Result:</span>{' '}
                  <span style={{ color: RARITY_COLORS[selectedRecipe.rarity] }}>
                    {selectedRecipe.resultQuantity > 1 ? `${selectedRecipe.resultQuantity}x ` : ''}
                    {selectedRecipe.resultType === 'module' ? 'Module' : selectedRecipe.resultType}
                  </span>
                </div>
                <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#8b949e' }}>Time:</span>{' '}
                  <span>{formatTime(selectedRecipe.craftingTime)}</span>
                </div>
                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                  <span style={{ color: '#8b949e' }}>Cost:</span>{' '}
                  <span style={{ color: pieces >= selectedRecipe.pieceCost ? '#2ecc71' : '#e74c3c' }}>
                    ₱{selectedRecipe.pieceCost}
                  </span>
                </div>

                <div style={styles.ingredientList}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#8b949e' }}>
                    Required Materials{craftQuantity > 1 ? ` (x${craftQuantity})` : ''}:
                  </div>
                  {selectedRecipe.ingredients.map((ing, i) => (
                    <div key={i} style={styles.ingredientItem}>
                      <span style={styles.ingredientName}>{ing.itemName || ing.itemId}</span>
                      <span style={{
                        ...styles.ingredientQty,
                        color: (ing.playerHas ?? 0) >= ing.quantity * craftQuantity ? '#2ecc71' : '#e74c3c',
                      }}>
                        {ing.playerHas !== undefined ? `${ing.playerHas}/` : ''}{ing.quantity * craftQuantity}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Quantity Selector */}
                {(() => {
                  const maxCraftable = getMaxCraftable(selectedRecipe);
                  return maxCraftable > 1 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12px', color: '#8b949e' }}>Quantity:</span>
                      <button
                        style={{
                          background: '#21262d',
                          border: '1px solid #30363d',
                          color: '#e6edf3',
                          width: '28px',
                          height: '28px',
                          cursor: craftQuantity > 1 ? 'pointer' : 'not-allowed',
                          opacity: craftQuantity > 1 ? 1 : 0.5,
                        }}
                        onClick={() => setCraftQuantity(Math.max(1, craftQuantity - 1))}
                        disabled={craftQuantity <= 1}
                      >
                        -
                      </button>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', minWidth: '30px', textAlign: 'center' }}>
                        {craftQuantity}
                      </span>
                      <button
                        style={{
                          background: '#21262d',
                          border: '1px solid #30363d',
                          color: '#e6edf3',
                          width: '28px',
                          height: '28px',
                          cursor: craftQuantity < maxCraftable ? 'pointer' : 'not-allowed',
                          opacity: craftQuantity < maxCraftable ? 1 : 0.5,
                        }}
                        onClick={() => setCraftQuantity(Math.min(maxCraftable, craftQuantity + 1))}
                        disabled={craftQuantity >= maxCraftable}
                      >
                        +
                      </button>
                      <button
                        style={{
                          background: '#21262d',
                          border: '1px solid #30363d',
                          color: '#58a6ff',
                          padding: '4px 8px',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                        onClick={() => setCraftQuantity(maxCraftable)}
                      >
                        MAX ({maxCraftable})
                      </button>
                    </div>
                  ) : null;
                })()}

                {selectedRecipe && !hasRequiredMaterials(selectedRecipe, craftQuantity) && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#f87171' }}>
                    Missing:{' '}
                    {getMissingMaterials(selectedRecipe, craftQuantity)
                      .map((m) => `${m.name} (${m.have}/${m.needed})`)
                      .join(', ')}
                  </div>
                )}

                {/* Total Cost Display */}
                {craftQuantity > 1 && (
                  <div style={{ fontSize: '12px', marginBottom: '8px', padding: '6px', background: '#161b22', border: '1px solid #30363d' }}>
                    <span style={{ color: '#8b949e' }}>Total Cost:</span>{' '}
                    <span style={{ color: pieces >= selectedRecipe.pieceCost * craftQuantity ? '#2ecc71' : '#e74c3c', fontWeight: 'bold' }}>
                      {(selectedRecipe.pieceCost * craftQuantity).toLocaleString()}₱
                    </span>
                    <span style={{ color: '#8b949e', marginLeft: '8px' }}>
                      → {selectedRecipe.resultQuantity * craftQuantity}x {selectedRecipe.name}
                    </span>
                  </div>
                )}

                <button
                  style={{
                    ...styles.craftBtn,
                    ...(canCraft(selectedRecipe) && craftQuantity <= getMaxCraftable(selectedRecipe) ? {} : styles.craftBtnDisabled),
                  }}
                  onClick={handleStartCraft}
                  disabled={!canCraft(selectedRecipe) || craftQuantity > getMaxCraftable(selectedRecipe)}
                >
                  {playerLevel < selectedRecipe.levelRequired
                    ? `REQUIRES LEVEL ${selectedRecipe.levelRequired}`
                    : pieces < selectedRecipe.pieceCost * craftQuantity
                      ? 'NOT ENOUGH PIECES'
                      : getMaxCraftable(selectedRecipe) === 0
                        ? 'MISSING MATERIALS'
                        : craftQuantity > 1
                          ? `CRAFT ${craftQuantity}x`
                          : 'START CRAFTING'}
                </button>
              </div>
            )}

            {/* Empty State */}
            {!selectedRecipe && !craftingStatus.isCrafting && (
              <div style={{ color: '#8b949e', textAlign: 'center', padding: '40px 20px' }}>
                Select a recipe to view details
              </div>
            )}

            {/* Active crafts list */}
            {(() => {
              const active = getActiveCraftsList();
              if (!active || active.length === 0) return null;
              return (
                <div style={styles.activeList}>
                  <div style={styles.activeListHeader}>
                    <span>Active Crafts</span>
                    <span style={{ color: '#58a6ff', fontWeight: 700 }}>{active.length}</span>
                  </div>
                  {active.map((c) => {
                    const recipe = recipes.find(r => r.id === c.recipeId);
                    const nowMs = clock;
                    const totalMs = Math.max(1, (c.endTime - c.startTime));
                    const elapsedMs = Math.min(totalMs, Math.max(0, nowMs - c.startTime));
                    const percent = Math.min(100, (elapsedMs / totalMs) * 100);
                    const remainingSec = Math.max(0, Math.ceil((c.endTime - nowMs) / 1000));
                    const qty = c.quantity || 1;
                    const resultQty = (c.resultQuantity || 1) * qty;
                    return (
                      <div key={`${c.recipeId}-${c.startTime}`} style={styles.activeItem}>
                        <div style={styles.activeItemHeader}>
                          <div style={{ ...styles.activeItemTitle, color: c.rarity ? RARITY_COLORS[c.rarity] : '#e6edf3' }}>
                            {c.recipeName}{qty > 1 ? ` x${qty}` : ''}
                          </div>
                          <div style={styles.activeItemTime}>
                            {remainingSec === 0 ? 'Finishing...' : `${formatTime(remainingSec)} left`}
                          </div>
                        </div>
                        <div style={{ ...styles.progressBar, marginTop: 6, height: 8, borderRadius: 0 }}>
                          <div
                            style={{
                              ...styles.progressFill,
                              width: `${percent}%`,
                              backgroundColor: remainingSec === 0 ? '#238636' : '#58a6ff',
                              borderRadius: 0,
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '11px', color: '#8b949e' }}>
                          <span>{recipe ? recipe.category.toUpperCase() : 'CRAFT'}</span>
                          <span>{resultQty > 1 ? `${resultQty}x output` : 'Single output'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CraftingPanel;
